import { baseGridDefaults } from './gridDefaults';
import type { GridConfig } from './gridConfigFactory';
import type { GridQueryInput } from './gridUtils';
import type { QueryResult } from '@flowqa/shared';
import type { SxProps, Theme } from '@mui/material/styles';
import type { QueryKey } from '@tanstack/react-query';

type TableGridOverridesWithConfig<TDto> = {
  gridConfig: GridConfig<TDto>;
  queryFn?: (input: GridQueryInput) => Promise<QueryResult<TDto>>;
  queryKey?: QueryKey;
  pageSizeOptions?: number[];
  initialPageSize?: number;
  disablePageSizeSelect?: boolean;
  hideFooter?: boolean;
  noRowsText?: string;
  onRowsChange?: (rows: TDto[]) => void;
  onQueryInput?: (input: GridQueryInput) => void;
  onDataUpdatedAt?: (timestamp: number | null) => void;
  showLastUpdated?: boolean;
  showToolbar?: boolean;
  chrome?: TableGridChromePreset;
};

type TableGridOverridesNoConfig<TDto> = {
  gridConfig?: undefined;
  queryFn: (input: GridQueryInput) => Promise<QueryResult<TDto>>;
  queryKey: QueryKey;
  pageSizeOptions?: number[];
  initialPageSize?: number;
  disablePageSizeSelect?: boolean;
  hideFooter?: boolean;
  noRowsText?: string;
  onRowsChange?: (rows: TDto[]) => void;
  onQueryInput?: (input: GridQueryInput) => void;
  onDataUpdatedAt?: (timestamp: number | null) => void;
  showLastUpdated?: boolean;
  showToolbar?: boolean;
  chrome?: TableGridChromePreset;
};

type TableGridOverrides<TDto> =
  | TableGridOverridesWithConfig<TDto>
  | TableGridOverridesNoConfig<TDto>;

type TableGridProps<TDto> = {
  queryFn: (input: GridQueryInput) => Promise<QueryResult<TDto>>;
  queryKey: QueryKey;
  pageSizeOptions: number[];
  initialPageSize?: number;
  disablePageSizeSelect?: boolean;
  hideFooter?: boolean;
  noRowsText: string;
  onRowsChange?: (rows: TDto[]) => void;
  onQueryInput?: (input: GridQueryInput) => void;
  onDataUpdatedAt?: (timestamp: number | null) => void;
  showLastUpdated?: boolean;
  showToolbar: boolean;
  disableCellFocus: true;
  autoHeight: true;
  skeletonGetRowHeight: null;
  containerSx?: SxProps<Theme>;
  sx?: SxProps<Theme>;
};

type TableGridChromePreset = 'default' | 'embedded';

type TableGridChrome = { containerSx?: SxProps<Theme>; sx?: SxProps<Theme> };

const resolveTableGridChrome = (preset?: TableGridChromePreset): TableGridChrome => {
  if (preset !== 'embedded') return {};
  return {};
};

export const buildTableGridProps = <TDto>({
  gridConfig,
  queryFn,
  queryKey,
  pageSizeOptions,
  initialPageSize,
  disablePageSizeSelect,
  hideFooter,
  noRowsText,
  onRowsChange,
  onQueryInput,
  onDataUpdatedAt,
  showLastUpdated,
  showToolbar,
  chrome,
}: TableGridOverrides<TDto>): TableGridProps<TDto> => {
  const resolvedQueryFn = queryFn ?? gridConfig?.queryFn;
  const resolvedQueryKey = queryKey ?? gridConfig?.queryKey;
  const resolvedPageSizeOptions =
    pageSizeOptions ?? gridConfig?.pageSizeOptions ?? baseGridDefaults.pageSizeOptions;
  const resolvedNoRowsText = noRowsText ?? gridConfig?.noRowsText ?? 'No results found';
  if (!resolvedQueryFn || !resolvedQueryKey) {
    throw new Error('buildTableGridProps requires queryFn and queryKey');
  }

  return {
    queryFn: resolvedQueryFn,
    queryKey: resolvedQueryKey,
    pageSizeOptions: resolvedPageSizeOptions,
    initialPageSize,
    disablePageSizeSelect,
    hideFooter,
    noRowsText: resolvedNoRowsText,
    onRowsChange,
    onQueryInput,
    onDataUpdatedAt,
    showLastUpdated,
    ...resolveTableGridChrome(chrome),
    showToolbar: showToolbar ?? false,
    disableCellFocus: true,
    autoHeight: true,
    skeletonGetRowHeight: null,
  };
};
